import styled from 'styled-components';

// Components
import { Typography } from '../../../../../components/display/Typography';


// ═══════════════════════════════════════════════════════════════════
// CONTAINER — espejo de `ContainerOT` (ShowOT/MainStyledComponents), sin el
// `max-width` restrictivo: esta card es la cabecera de la página del RV
// (Task 3 la monta a ancho completo), no una fila anidada dentro de un tab.
// ═══════════════════════════════════════════════════════════════════

export const Container = styled.div`
  width: 100%;
  /* backgroundLila — el color de card del proyecto (SectionCard, FormSection
     del wizard), NO gris (feedback usuario 2026-07-07). */
  background-color: ${({ theme }) => theme.colors.backgroundLila};
  border-radius: 0.8rem;
  padding: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    padding: ${({ theme }) => theme.spacing.l};
  }
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    padding: ${({ theme }) => theme.spacing.m};
  }
`;

// ═══════════════════════════════════════════════════════════════════
// HEADER ROW — espejo de `ContainerHeader`/`TitleOrder`/`ContainerRefIcon`/
// `RightArrow` (ShowOT/WorkOrder): nombre + ROID + pill de estado + chevron,
// toda la fila hace toggle del contenido expandido.
// ═══════════════════════════════════════════════════════════════════

export const HeaderRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.m};
  cursor: pointer;
`;

export const RouteTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.L};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.M};
  }
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    gap: ${({ theme }) => theme.spacing.xxxs};
  }
`;

export const RoidText = styled(Typography)`
  font-size: ${({ theme }) => theme.fontSize.S};
`;

export const Chevron = styled.img<{ $showInfo: boolean }>`
  transform: ${({ $showInfo }) => ($showInfo ? 'rotate(90deg)' : 'none')};
  width: 1.4rem;
  height: 1rem;
  flex-shrink: 0;
`;

// La pill de estado es el `SpecialTag` canónico (mismo componente y colores
// que la tabla de work-orders), vía `mapRouteStateToTagVariant` — el antiguo
// `StatusBadge` con colores propios se eliminó (feedback usuario 2026-07-07:
// reusar componentes/colores de la app, no inventar). Sigue READ-ONLY
// (review final C2: UPDATE_ORDER genérico no soporta estados de RV).

// ═══════════════════════════════════════════════════════════════════
// CONTENT GRID — espejo de `ContainerData` (ShowOT/MainStyledComponents):
// grid flex-wrap de `Definition` (fecha apertura/fin, concesión, carretera,
// PKs, equipo, fecha de creación).
// ═══════════════════════════════════════════════════════════════════

export const ContentGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.l};
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.l};

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    gap: ${({ theme }) => theme.spacing.m};
    margin-top: ${({ theme }) => theme.spacing.m};
  }
`;

// ═══════════════════════════════════════════════════════════════════
// CREW ROWS — filas "Operarios" y "Vehículos" (misma estructura de fila:
// label con el patrón `dt` de `Definition` + entradas de solo lectura).
// Operarios: chips (sin acción de quitar, a diferencia de
// `MultiSelectDropdown.Tag`). Vehículos: texto `plate (driverName)`.
// ═══════════════════════════════════════════════════════════════════

export const CrewRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xs};
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.m};
  flex-wrap: wrap;
`;

export const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

// Pill de solo lectura: misma geometría que `SpecialTag`/`PauseCauseChip`
// (border-radius 100rem, padding equivalente, fontSize.XS) con el fondo
// neutro `backgroundLila` que usa `MultiSelectDropdown.Tag` para valores
// seleccionados — combina las 2 referencias de chip que cita el brief, sin
// el botón de quitar (aquí es de solo lectura).
export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  width: max-content;
  padding: 0.4rem 1.6rem;
  border-radius: 100rem;
  font-size: ${({ theme }) => theme.fontSize.XS};
  background: ${({ theme }) => theme.colors.backgroundLila};
  color: ${({ theme }) => theme.colors.black};
`;

// ═══════════════════════════════════════════════════════════════════
// ACTIONS — botón "Finalizar recorrido", alineado a la derecha dentro de
// la card (mismo `MainButton` que usaba `RouteControlPanel`).
// ═══════════════════════════════════════════════════════════════════

export const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.m};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing.l};

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    margin-top: ${({ theme }) => theme.spacing.m};
  }
`;
